// @flow
import * as React from 'react';
import * as Yup from 'yup';
import {
    injectIntl, defineMessages, FormattedMessage,
} from 'react-intl';
import { Anchor, Box, Text } from 'grommet';
import { useDropzone } from 'react-dropzone';
import FieldWrapper from '../FieldWrapper';
import File from './File';


type Props = {
    field: {
        name: string,
        value: Array<Object>,
    },
    form: {
        errors: Object,
        touched: Object,
        isSubmitting: boolean,
        setFieldValue: Function,
    },
    required: boolean,
    label: string,
    help_text: string,

    accept?: string,
    maxSize?: number,
    minSize?: number,
    disabled?: boolean,
    multiple?: boolean,
    showPreview: boolean,
    showFileSize: boolean,
}


const FileInput = (props: Props): React.Node => {
    const {
        field: {
            name,
            value,
        },
        form: {
            isSubmitting, setFieldValue,
        },
        label,
        help_text: help,
        required,
        showPreview,
        showFileSize,
        multiple,
        disabled: disabledProp,
        accept,
        maxSize,
        minSize,
    } = props;

    const [disabled, setDisabled] = React.useState(disabledProp);

    const onDrop = React.useCallback(
        (acceptedFiles) => {
            if (!disabled) {
                if (showPreview) {
                    // Accepted files is read-only.
                    acceptedFiles.map((file) => Object.assign(file, {
                        preview: URL.createObjectURL(file),
                    }));
                }
                if (value) {
                    setFieldValue(name, [...value, ...acceptedFiles]);
                } else {
                    setFieldValue(name, acceptedFiles);
                }
                if (!multiple && value?.length === 0 && acceptedFiles.length > 0) {
                    // When only single file allowed
                    setDisabled(true);
                }
            }
        },
        [disabled, showPreview, multiple, value, name, setFieldValue],
    );

    // Handler for 'X' button click
    const removeFile = (file) => {
        const newFiles = [...value];
        newFiles.splice(newFiles.indexOf(file), 1);
        setFieldValue(name, newFiles);
        if (newFiles.length === 0) {
            setDisabled(false);
        }
    };

    const currentFiles = value?.map((file, index) => (
        <File
            file={file}
            key={index.toString()}
            removeFile={removeFile}
            showPreview={showPreview}
            showFileSize={showFileSize}
            margin={{
                // To maintain vertical centering, the last file shouldn't
                // have a bottom margin
                bottom: index !== value.length - 1 ? 'xsmall' : 'none',
            }}
        />
    )) || [];

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept, // Accept specific file types
        maxSize, // Max file size in bytes
        minSize, // Min file size in bytes
        multiple, // Accept multiple files
        noClick: value?.length, // If files are in dropzone, disable click
        onDrop, // onDrop handler
    });

    return (
        <FieldWrapper
            name={name}
            required={required}
            helpText={help}
            label={label}
        >
            <Box
                background={isDragActive || value?.length ? 'white' : 'light-2'}
                border={{
                    color: isDragActive ? 'brand' : 'light-5',
                    style: 'dashed',
                    size: 'small',
                }}
                height={{
                    min: 'small',
                }}
                align={value?.length ? 'stretch' : 'center'}
                justify="center"
                pad="xsmall"
                {...getRootProps()}
            >
                {/* This input is required by react-dropzone and expands to fill its bouding
       *  container. The UI is created by the elements below the input.
       */}
                <input {...getInputProps()} disabled={disabled || isSubmitting} />
                {value?.length ? currentFiles : undefined}
                {!value?.length
                && (!isDragActive ? (
                    <Text>
                        <FormattedMessage
                            id="file-upload-dnd-message"
                            defaultMessage="Drag and drop or {choose} {count, plural, one {a file}
                            other {files}} (max.size {size} MB)"
                            values={{
                                choose:
                        <Anchor>
                            <FormattedMessage id="file-upload-choose-file" defaultMessage="choose" />
                        </Anchor>,
                                count: multiple ? 2 : 1,
                                size: maxSize && maxSize / 1048576,
                            }}
                        />
                    </Text>
                ) : (
                    <FormattedMessage id="file-upload-files" defaultMessage="Files" />
                ))}
            </Box>
        </FieldWrapper>
    );
};

const messages = defineMessages({
    required: {
        id: 'fileinput-required',
        defaultMessage: 'This field is required.',
    },
});

FileInput.getValidationSchema = (field: { validation: Object }) => {
    let errMsg = Yup.string().nullable();
    if (field.validation.required) {
        errMsg = Yup.string().required(messages.required).nullable();
    }
    return errMsg;
};

FileInput.defaultProps = {
    disabled: false,
    accept: '',
    maxSize: 10485760, // 10 MB
    minSize: undefined,
    multiple: false,
    showPreview: true,
    showFileSize: false,
};

export default injectIntl(FileInput);
